import React, { useEffect, useState } from 'react';
import { useTheme, useLocation } from '../App';
import { ArrowUp, ArrowDown, Minus, Settings, Maximize, Minimize, Info } from 'lucide-react';
import LeversSection from './LeversSection';
import KPICustomizeModal, { KPICategory, KPIItem } from './KPICustomizeModal';
// Add KPI definitions
export const kpiDefinitions: Record<string, string> = {
  totalSales: 'Total revenue from all sales before taxes and adjustments',
  transactions: 'Total number of completed transactions',
  avgBasket: 'Average dollar amount per transaction (Total Sales ÷ Transactions)',
  newCustomers: 'Number of first-time customers in the period',
  margin: 'Gross profit margin: (Revenue – COGS) ÷ Revenue × 100',
  inventoryValue: 'Total wholesale cost of inventory on hand / cash basis',
  complianceScore: 'Percentage of required checks/audits passed (e.g. inventory, POS)',
  openComplianceIssues: 'Number of unresolved compliance issues requiring attention',
  shrinkageRate: '(Recorded loss ÷ Starting Inventory) × 100',
  avgWaitTime: 'Average time customers wait before being served',
  fulfillmentSpeed: 'Average minutes from order placed to ready for pickup',
  cartAbandonment: '(Carts started – Checkouts completed) ÷ Carts started',
  revenueGrowth: '(Current Period – Prior Period) ÷ Prior Period × 100',
  inventoryTurnover: 'How many times you sold & replenished your stock (×/month)',
  daysOnHand: 'Average days remaining across all inventory stock',
  agingInventory: 'Percentage of stock older than target shelf-life (e.g., 30 days)',
  reorderCompliance: 'Percentage of reorder points hit that triggered a purchase order on time',
  overstock: 'Percentage of inventory exceeding target par levels',
  expiredRate: 'Percentage of inventory that expired before selling',
  avgProductGrade: 'Weighted average grade (A-F) of products based on profitability and sale rates',
  newMembers: 'Number of new members registered in the period',
  loyaltyMembers: 'Members with complete profiles, contact and opt-in',
  customerLTV: "Average revenue per customer over their 'lifetime' period",
  churnRate: 'Percentage of base that did not make repeat purchase in 90 days',
  activeCustomer: 'Percentage of total database who have purchased in last 30 days',
  atRiskCustomer: "Percentage of customers whose last purchase falls into the 'at-risk' recency band (e.g., 31–90 days)",
  winBackRate: 'Number of at-risk or dormant customers who return after a re-engagement campaign ÷ number targeted',
  avgOrderValue: 'Average dollar amount per transaction (Total Sales ÷ Transactions)',
  customersInQueue: 'Current number of customers waiting to be served',
  avgOrderTime: 'Average time to complete an order from start to finish',
  avgTimeInStore: 'Average duration of customer visits in the store',
  avgUnitsPerTx: 'Average number of items purchased per transaction',
  avgDiscount: 'Average discount percentage applied to a cart before taxes',
  housePercent: 'Percentage of house brand products sold by quantity',
  batchesWithVariance: 'Number of batches with discrepancy between system and physical count',
  excessiveAdjustments: 'Adjustments exceeding a set percentage threshold',
  avgOnlineOrderValue: 'Gross Receipts (e-commerce) ÷ Online Transactions',
  mobileVsDesktop: 'Percentage split of visits by device type',
  dualCommerceCustomers: 'Percentage of in-store visitors who also shop online'
};
interface KPITileProps {
  title: string;
  value: string | number;
  trend: number;
  comparisonPeriod: string;
  onClick: () => void;
  definition?: string;
}
const KPITile: React.FC<KPITileProps> = ({
  title,
  value,
  trend,
  comparisonPeriod,
  onClick,
  definition
}) => {
  const {
    isDarkMode
  } = useTheme();
  const [showTooltip, setShowTooltip] = useState(false);
  const getTrendIcon = () => {
    if (trend > 0) return <ArrowUp size={16} className="text-green-500" data-id="element-235" />;
    if (trend < 0) return <ArrowDown size={16} className="text-red-500" data-id="element-236" />;
    return <Minus size={16} className="text-gray-500" data-id="element-237" />;
  };
  const getTrendColor = () => {
    if (trend > 0) return 'text-green-500 border-green-500';
    if (trend < 0) return 'text-red-500 border-red-500';
    return 'text-gray-500';
  };
  const getBgColor = () => {
    if (isDarkMode) {
      if (trend > 0) return 'bg-[#0d1117] border-l-4 border-green-500';
      if (trend < 0) return 'bg-[#0d1117] border-l-4 border-red-500';
      return 'bg-[#0d1117]';
    } else {
      if (trend > 0) return 'bg-white border-l-4 border-green-500';
      if (trend < 0) return 'bg-white border-l-4 border-red-500';
      return 'bg-white';
    }
  };
  return <div className={`p-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow ${getBgColor()}`} onClick={onClick} data-id="element-238">
      <div className="flex items-center justify-between" data-id="element-239">
        <h3 className="text-sm font-medium opacity-80" data-id="element-240">{title}</h3>
        {definition && <div className="relative" data-id="element-241">
            <button className="text-gray-400 hover:text-gray-300" onClick={e => {
          e.stopPropagation();
          setShowTooltip(!showTooltip);
        }} data-id="element-242">
              <Info size={14} data-id="element-243" />
            </button>
            {showTooltip && <div className={`absolute right-0 top-6 z-10 w-64 p-2 text-xs rounded shadow-lg ${isDarkMode ? 'bg-[#222930] text-white' : 'bg-white text-black'}`} data-id="element-244">
                {definition}
              </div>}
          </div>}
      </div>
      <p className="text-2xl font-bold mt-1" data-id="element-245">{value}</p>
      <div className={`flex items-center mt-1 text-sm ${getTrendColor()}`} data-id="element-246">
        {getTrendIcon()}
        <span className="ml-1" data-id="element-247">
          {trend > 0 ? '+' : ''}
          {trend}% vs {comparisonPeriod}
        </span>
      </div>
    </div>;
};
const KPIs = () => {
  const {
    isDarkMode
  } = useTheme();
  const {
    currentLocation
  } = useLocation();
  const [timeframe, setTimeframe] = useState('today');
  const [viewMode, setViewMode] = useState('current');
  const [selectedKPI, setSelectedKPI] = useState<string | null>(null);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Map to match KPI titles with their IDs for easier matching
  const kpiTitleToIdMap: Record<string, string> = {
    'Total Sales': 'totalSales',
    'Avg Basket': 'avgBasket',
    Transactions: 'transactions',
    'New Customers': 'newCustomers',
    Margin: 'margin',
    'Inventory Value': 'inventoryValue',
    'Compliance Score': 'complianceScore',
    'Open Compliance Issues': 'openComplianceIssues',
    'Shrinkage Rate': 'shrinkageRate',
    'Average Wait Time': 'avgWaitTime',
    'Fulfillment Speed': 'fulfillmentSpeed',
    'Cart Abandonment %': 'cartAbandonment',
    'Revenue Growth %': 'revenueGrowth',
    'Inventory Turnover Rate': 'inventoryTurnover',
    'Average Days on Hand': 'daysOnHand',
    'Aging Inventory %': 'agingInventory',
    'Reorder Compliance %': 'reorderCompliance',
    'Overstock %': 'overstock',
    'Expired Rate': 'expiredRate',
    'Average Product Grade': 'avgProductGrade',
    'New Members': 'newMembers',
    'New Loyalty Members': 'loyaltyMembers',
    'Customer Lifetime Value (CLV)': 'customerLTV',
    'Churn Rate %': 'churnRate',
    'Active Customer %': 'activeCustomer',
    'At-Risk Customer %': 'atRiskCustomer',
    'Win-Back Rate': 'winBackRate',
    'Average Order Value': 'avgOrderValue',
    'Customers In Queue': 'customersInQueue',
    'Average Order Time': 'avgOrderTime',
    'Average Time in Store': 'avgTimeInStore',
    'Average Units per Transaction': 'avgUnitsPerTx',
    'Average Discount %': 'avgDiscount',
    'House Percent (by qty)': 'housePercent',
    'Batches with Compliance Variance': 'batchesWithVariance',
    'Excessive Adjustments': 'excessiveAdjustments',
    'Average Online Order Value': 'avgOnlineOrderValue',
    'Mobile-vs-Desktop Traffic Split': 'mobileVsDesktop',
    'Dual Commerce Customers': 'dualCommerceCustomers'
  };
  // Reverse mapping from ID to title
  const kpiIdToTitleMap: Record<string, string> = Object.entries(kpiTitleToIdMap).reduce((acc, [title, id]) => {
    acc[id] = title;
    return acc;
  }, {} as Record<string, string>);
  // Define all available KPIs by category
  const [availableKPIs, setAvailableKPIs] = useState<Record<string, KPIItem[]>>({
    management: [{
      id: 'totalSales',
      title: 'Total Sales',
      selected: true
    }, {
      id: 'transactions',
      title: 'Transactions',
      selected: true
    }, {
      id: 'avgBasket',
      title: 'Avg Basket',
      selected: true
    }, {
      id: 'newCustomers',
      title: 'New Customers',
      selected: true
    }, {
      id: 'margin',
      title: 'Margin',
      selected: true
    }, {
      id: 'revenueGrowth',
      title: 'Revenue Growth %',
      selected: false
    }],
    inventory: [{
      id: 'inventoryValue',
      title: 'Inventory Value',
      selected: true
    }, {
      id: 'inventoryTurnover',
      title: 'Inventory Turnover Rate',
      selected: false
    }, {
      id: 'daysOnHand',
      title: 'Average Days on Hand',
      selected: false
    }, {
      id: 'shrinkageRate',
      title: 'Shrinkage Rate',
      selected: false
    }, {
      id: 'agingInventory',
      title: 'Aging Inventory %',
      selected: false
    }, {
      id: 'reorderCompliance',
      title: 'Reorder Compliance %',
      selected: false
    }, {
      id: 'overstock',
      title: 'Overstock %',
      selected: false
    }, {
      id: 'expiredRate',
      title: 'Expired Rate',
      selected: false
    }, {
      id: 'avgProductGrade',
      title: 'Average Product Grade',
      selected: false
    }],
    marketing: [{
      id: 'newMembers',
      title: 'New Members',
      selected: false
    }, {
      id: 'loyaltyMembers',
      title: 'New Loyalty Members',
      selected: false
    }, {
      id: 'customerLTV',
      title: 'Customer Lifetime Value (CLV)',
      selected: false
    }, {
      id: 'churnRate',
      title: 'Churn Rate %',
      selected: false
    }, {
      id: 'activeCustomer',
      title: 'Active Customer %',
      selected: false
    }, {
      id: 'atRiskCustomer',
      title: 'At-Risk Customer %',
      selected: false
    }, {
      id: 'winBackRate',
      title: 'Win-Back Rate',
      selected: false
    }],
    sales: [{
      id: 'avgOrderValue',
      title: 'Average Order Value',
      selected: false
    }],
    overview: [{
      id: 'customersInQueue',
      title: 'Customers In Queue',
      selected: false
    }, {
      id: 'avgWaitTime',
      title: 'Average Wait Time',
      selected: false
    }, {
      id: 'avgOrderTime',
      title: 'Average Order Time',
      selected: false
    }, {
      id: 'avgTimeInStore',
      title: 'Average Time in Store',
      selected: false
    }, {
      id: 'avgUnitsPerTx',
      title: 'Average Units per Transaction',
      selected: false
    }, {
      id: 'avgDiscount',
      title: 'Average Discount %',
      selected: false
    }, {
      id: 'housePercent',
      title: 'House Percent (by qty)',
      selected: false
    }],
    compliance: [{
      id: 'complianceScore',
      title: 'Compliance Score',
      selected: false
    }, {
      id: 'openComplianceIssues',
      title: 'Open Compliance Issues',
      selected: false
    }, {
      id: 'batchesWithVariance',
      title: 'Batches with Compliance Variance',
      selected: false
    }, {
      id: 'excessiveAdjustments',
      title: 'Excessive Adjustments',
      selected: false
    }],
    ecommerce: [{
      id: 'fulfillmentSpeed',
      title: 'Fulfillment Speed',
      selected: false
    }, {
      id: 'avgOnlineOrderValue',
      title: 'Average Online Order Value',
      selected: false
    }, {
      id: 'cartAbandonment',
      title: 'Cart Abandonment %',
      selected: false
    }, {
      id: 'mobileVsDesktop',
      title: 'Mobile-vs-Desktop Traffic Split',
      selected: false
    }, {
      id: 'dualCommerceCustomers',
      title: 'Dual Commerce Customers',
      selected: false
    }]
  });
  // Count total enabled KPIs
  const countEnabledKPIs = () => {
    return Object.values(availableKPIs).reduce((total, kpis) => {
      return total + kpis.filter(kpi => kpi.selected).length;
    }, 0);
  };
  // Sample KPI data for different locations
  const kpiData = {
    Fresno: {
      today: [{
        title: 'Total Sales',
        value: '$12,450',
        trend: -8,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Avg Basket',
        value: '$68.75',
        trend: 5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Transactions',
        value: '187',
        trend: -12,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'New Customers',
        value: '24',
        trend: 15,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Margin',
        value: '42%',
        trend: -2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Inventory Value',
        value: '$145,320',
        trend: 0,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Compliance Score',
        value: '94%',
        trend: 2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Open Compliance Issues',
        value: '3',
        trend: -25,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Shrinkage Rate',
        value: '1.2%',
        trend: -0.3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Wait Time',
        value: '4.5 min',
        trend: 12,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Fulfillment Speed',
        value: '8.2 min',
        trend: -5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Cart Abandonment %',
        value: '32%',
        trend: 3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Revenue Growth %',
        value: '5.8%',
        trend: 1.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Inventory Turnover Rate',
        value: '2.3×',
        trend: 0.4,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Days on Hand',
        value: '18.5',
        trend: -2.1,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Aging Inventory %',
        value: '14.2%',
        trend: 2.3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Reorder Compliance %',
        value: '87%',
        trend: 3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Overstock %',
        value: '12%',
        trend: -1.5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Expired Rate',
        value: '0.8%',
        trend: -0.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Product Grade',
        value: 'B+',
        trend: 0,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'New Members',
        value: '18',
        trend: 12.5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'New Loyalty Members',
        value: '7',
        trend: -5.4,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Customer Lifetime Value (CLV)',
        value: '$420',
        trend: 1.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Churn Rate %',
        value: '3.2%',
        trend: -0.4,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Active Customer %',
        value: '42%',
        trend: 2.1,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'At-Risk Customer %',
        value: '18%',
        trend: -1.5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Win-Back Rate',
        value: '12%',
        trend: 3.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Order Value',
        value: '$72.35',
        trend: 3.8,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Customers In Queue',
        value: '5',
        trend: 25,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Order Time',
        value: '6.2 min',
        trend: -8.3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Time in Store',
        value: '15.8 min',
        trend: -4.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Units per Transaction',
        value: '2.8',
        trend: 5.7,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Discount %',
        value: '8.5%',
        trend: 1.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'House Percent (by qty)',
        value: '32%',
        trend: 3.5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Batches with Compliance Variance',
        value: '2',
        trend: -33.3,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Excessive Adjustments',
        value: '3',
        trend: 0,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Average Online Order Value',
        value: '$85.40',
        trend: 4.2,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Mobile-vs-Desktop Traffic Split',
        value: '65/35',
        trend: 5,
        comparisonPeriod: 'yesterday'
      }, {
        title: 'Dual Commerce Customers',
        value: '23%',
        trend: 2.8,
        comparisonPeriod: 'yesterday'
      }],
      week: [{
        title: 'Total Sales',
        value: '$78,340',
        trend: 3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Avg Basket',
        value: '$72.15',
        trend: 8,
        comparisonPeriod: 'last week'
      }, {
        title: 'Transactions',
        value: '1,085',
        trend: -5,
        comparisonPeriod: 'last week'
      }, {
        title: 'New Customers',
        value: '142',
        trend: 22,
        comparisonPeriod: 'last week'
      }, {
        title: 'Margin',
        value: '43%',
        trend: 1,
        comparisonPeriod: 'last week'
      }, {
        title: 'Inventory Value',
        value: '$145,320',
        trend: -3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Compliance Score',
        value: '93%',
        trend: 1,
        comparisonPeriod: 'last week'
      }, {
        title: 'Open Compliance Issues',
        value: '4',
        trend: -20,
        comparisonPeriod: 'last week'
      }, {
        title: 'Shrinkage Rate',
        value: '1.3%',
        trend: -0.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Wait Time',
        value: '4.2 min',
        trend: 5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Fulfillment Speed',
        value: '8.5 min',
        trend: -3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Cart Abandonment %',
        value: '30%',
        trend: -2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Revenue Growth %',
        value: '6.2%',
        trend: 0.8,
        comparisonPeriod: 'last week'
      }, {
        title: 'Inventory Turnover Rate',
        value: '2.5×',
        trend: 0.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Days on Hand',
        value: '17.8',
        trend: -1.8,
        comparisonPeriod: 'last week'
      }, {
        title: 'Aging Inventory %',
        value: '13.5%',
        trend: 1.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Reorder Compliance %',
        value: '89%',
        trend: 2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Overstock %',
        value: '11%',
        trend: -2.1,
        comparisonPeriod: 'last week'
      }, {
        title: 'Expired Rate',
        value: '0.7%',
        trend: -0.3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Product Grade',
        value: 'B+',
        trend: 0,
        comparisonPeriod: 'last week'
      }, {
        title: 'New Members',
        value: '95',
        trend: 15.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'New Loyalty Members',
        value: '42',
        trend: 10.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Customer Lifetime Value (CLV)',
        value: '$435',
        trend: 1.8,
        comparisonPeriod: 'last week'
      }, {
        title: 'Churn Rate %',
        value: '3.0%',
        trend: -0.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Active Customer %',
        value: '44%',
        trend: 3.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'At-Risk Customer %',
        value: '17%',
        trend: -2.1,
        comparisonPeriod: 'last week'
      }, {
        title: 'Win-Back Rate',
        value: '14%',
        trend: 2.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Order Value',
        value: '$74.20',
        trend: 2.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Customers In Queue',
        value: '4',
        trend: -20,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Order Time',
        value: '5.8 min',
        trend: -7.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Time in Store',
        value: '14.5 min',
        trend: -3.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Units per Transaction',
        value: '3.0',
        trend: 4.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Discount %',
        value: '7.8%',
        trend: -0.8,
        comparisonPeriod: 'last week'
      }, {
        title: 'House Percent (by qty)',
        value: '34%',
        trend: 4.2,
        comparisonPeriod: 'last week'
      }, {
        title: 'Batches with Compliance Variance',
        value: '3',
        trend: 0,
        comparisonPeriod: 'last week'
      }, {
        title: 'Excessive Adjustments',
        value: '2',
        trend: -33.3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Average Online Order Value',
        value: '$88.20',
        trend: 3.5,
        comparisonPeriod: 'last week'
      }, {
        title: 'Mobile-vs-Desktop Traffic Split',
        value: '63/37',
        trend: 3,
        comparisonPeriod: 'last week'
      }, {
        title: 'Dual Commerce Customers',
        value: '25%',
        trend: 3.2,
        comparisonPeriod: 'last week'
      }],
      month: [{
        title: 'Total Sales',
        value: '$342,780',
        trend: 12,
        comparisonPeriod: 'last month'
      }, {
        title: 'Avg Basket',
        value: '$70.45',
        trend: 6,
        comparisonPeriod: 'last month'
      }, {
        title: 'Transactions',
        value: '4,865',
        trend: 4,
        comparisonPeriod: 'last month'
      }, {
        title: 'New Customers',
        value: '625',
        trend: 18,
        comparisonPeriod: 'last month'
      }, {
        title: 'Margin',
        value: '44%',
        trend: 3,
        comparisonPeriod: 'last month'
      }, {
        title: 'Inventory Value',
        value: '$145,320',
        trend: -5,
        comparisonPeriod: 'last month'
      }, {
        title: 'Compliance Score',
        value: '92%',
        trend: 3,
        comparisonPeriod: 'last month'
      }, {
        title: 'Open Compliance Issues',
        value: '5',
        trend: -28,
        comparisonPeriod: 'last month'
      }, {
        title: 'Shrinkage Rate',
        value: '1.4%',
        trend: -0.4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Wait Time',
        value: '4.8 min',
        trend: -10,
        comparisonPeriod: 'last month'
      }, {
        title: 'Fulfillment Speed',
        value: '9.1 min',
        trend: -8,
        comparisonPeriod: 'last month'
      }, {
        title: 'Cart Abandonment %',
        value: '33%',
        trend: -4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Revenue Growth %',
        value: '7.5%',
        trend: 2.1,
        comparisonPeriod: 'last month'
      }, {
        title: 'Inventory Turnover Rate',
        value: '2.8×',
        trend: 0.5,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Days on Hand',
        value: '16.2',
        trend: -3.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Aging Inventory %',
        value: '12.1%',
        trend: -2.4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Reorder Compliance %',
        value: '92%',
        trend: 4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Overstock %',
        value: '9.5%',
        trend: -3.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Expired Rate',
        value: '0.5%',
        trend: -0.4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Product Grade',
        value: 'A-',
        trend: 5,
        comparisonPeriod: 'last month'
      }, {
        title: 'New Members',
        value: '410',
        trend: 22.4,
        comparisonPeriod: 'last month'
      }, {
        title: 'New Loyalty Members',
        value: '185',
        trend: 15.6,
        comparisonPeriod: 'last month'
      }, {
        title: 'Customer Lifetime Value (CLV)',
        value: '$455',
        trend: 3.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Churn Rate %',
        value: '2.8%',
        trend: -0.7,
        comparisonPeriod: 'last month'
      }, {
        title: 'Active Customer %',
        value: '48%',
        trend: 5.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'At-Risk Customer %',
        value: '15%',
        trend: -3.5,
        comparisonPeriod: 'last month'
      }, {
        title: 'Win-Back Rate',
        value: '16%',
        trend: 4.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Order Value',
        value: '$71.50',
        trend: 4.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Customers In Queue',
        value: '4',
        trend: -33.3,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Order Time',
        value: '5.2 min',
        trend: -12.5,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Time in Store',
        value: '13.2 min',
        trend: -8.4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Units per Transaction',
        value: '3.2',
        trend: 6.5,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Discount %',
        value: '6.5%',
        trend: -2.5,
        comparisonPeriod: 'last month'
      }, {
        title: 'House Percent (by qty)',
        value: '38%',
        trend: 8.2,
        comparisonPeriod: 'last month'
      }, {
        title: 'Batches with Compliance Variance',
        value: '4',
        trend: -20,
        comparisonPeriod: 'last month'
      }, {
        title: 'Excessive Adjustments',
        value: '1',
        trend: -50,
        comparisonPeriod: 'last month'
      }, {
        title: 'Average Online Order Value',
        value: '$92.50',
        trend: 5.8,
        comparisonPeriod: 'last month'
      }, {
        title: 'Mobile-vs-Desktop Traffic Split',
        value: '62/38',
        trend: 4,
        comparisonPeriod: 'last month'
      }, {
        title: 'Dual Commerce Customers',
        value: '28%',
        trend: 5.5,
        comparisonPeriod: 'last month'
      }]
    }
    // Other locations data would be here...
  };
  const allShopsData = {
    // This would contain aggregated data for all shops...
    today: [{
      title: 'Total Sales',
      value: '$56,390',
      trend: -3,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Avg Basket',
      value: '$72.24',
      trend: 3,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Transactions',
      value: '776',
      trend: -7,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'New Customers',
      value: '112',
      trend: 15,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Margin',
      value: '40%',
      trend: -2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Inventory Value',
      value: '$645,360',
      trend: 0,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Compliance Score',
      value: '91%',
      trend: 0,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Open Compliance Issues',
      value: '12',
      trend: -15,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Shrinkage Rate',
      value: '1.3%',
      trend: -0.1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Wait Time',
      value: '4.7 min',
      trend: 8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Fulfillment Speed',
      value: '8.9 min',
      trend: -2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Cart Abandonment %',
      value: '31%',
      trend: 1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Revenue Growth %',
      value: '4.2%',
      trend: -0.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Inventory Turnover Rate',
      value: '2.1×',
      trend: 0.2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Days on Hand',
      value: '19.2',
      trend: -1.5,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Aging Inventory %',
      value: '15.3%',
      trend: 1.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Reorder Compliance %',
      value: '85%',
      trend: 2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Overstock %',
      value: '13.5%',
      trend: -1.2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Expired Rate',
      value: '0.9%',
      trend: -0.1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Product Grade',
      value: 'B',
      trend: 0,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'New Members',
      value: '85',
      trend: 8.9,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'New Loyalty Members',
      value: '32',
      trend: -3.0,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Customer Lifetime Value (CLV)',
      value: '$405',
      trend: 0.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Churn Rate %',
      value: '3.5%',
      trend: -0.2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Active Customer %',
      value: '40%',
      trend: 1.5,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'At-Risk Customer %',
      value: '19.5%',
      trend: -0.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Win-Back Rate',
      value: '10.5%',
      trend: 2.1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Order Value',
      value: '$75.10',
      trend: 1.5,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Customers In Queue',
      value: '18',
      trend: 12.5,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Order Time',
      value: '6.8 min',
      trend: -5.2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Time in Store',
      value: '16.5 min',
      trend: -2.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Units per Transaction',
      value: '2.6',
      trend: 3.2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Discount %',
      value: '9.2%',
      trend: 0.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'House Percent (by qty)',
      value: '30%',
      trend: 2.1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Batches with Compliance Variance',
      value: '8',
      trend: -11.1,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Excessive Adjustments',
      value: '5',
      trend: -16.7,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Average Online Order Value',
      value: '$82.30',
      trend: 2.8,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Mobile-vs-Desktop Traffic Split',
      value: '68/32',
      trend: 2,
      comparisonPeriod: 'yesterday'
    }, {
      title: 'Dual Commerce Customers',
      value: '21%',
      trend: 1.5,
      comparisonPeriod: 'yesterday'
    }],
    week: [
    // Weekly data for all shops...
    {
      title: 'Total Sales',
      value: '$385,240',
      trend: 5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Avg Basket',
      value: '$73.45',
      trend: 4,
      comparisonPeriod: 'last week'
    }, {
      title: 'Transactions',
      value: '5,245',
      trend: 1,
      comparisonPeriod: 'last week'
    }, {
      title: 'New Customers',
      value: '580',
      trend: 12,
      comparisonPeriod: 'last week'
    }, {
      title: 'Margin',
      value: '41%',
      trend: 0.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Inventory Value',
      value: '$645,360',
      trend: -2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Compliance Score',
      value: '92%',
      trend: 1,
      comparisonPeriod: 'last week'
    }, {
      title: 'Open Compliance Issues',
      value: '10',
      trend: -16.7,
      comparisonPeriod: 'last week'
    }, {
      title: 'Shrinkage Rate',
      value: '1.2%',
      trend: -0.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Wait Time',
      value: '4.5 min',
      trend: 4,
      comparisonPeriod: 'last week'
    }, {
      title: 'Fulfillment Speed',
      value: '8.7 min',
      trend: -1.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Cart Abandonment %',
      value: '29%',
      trend: -1.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Revenue Growth %',
      value: '5.1%',
      trend: 0.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Inventory Turnover Rate',
      value: '2.3×',
      trend: 0.3,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Days on Hand',
      value: '18.5',
      trend: -1.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Aging Inventory %',
      value: '14.2%',
      trend: 0.8,
      comparisonPeriod: 'last week'
    }, {
      title: 'Reorder Compliance %',
      value: '87%',
      trend: 3,
      comparisonPeriod: 'last week'
    }, {
      title: 'Overstock %',
      value: '12.2%',
      trend: -1.8,
      comparisonPeriod: 'last week'
    }, {
      title: 'Expired Rate',
      value: '0.8%',
      trend: -0.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Product Grade',
      value: 'B+',
      trend: 2,
      comparisonPeriod: 'last week'
    }, {
      title: 'New Members',
      value: '430',
      trend: 10.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'New Loyalty Members',
      value: '185',
      trend: 5.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Customer Lifetime Value (CLV)',
      value: '$415',
      trend: 1.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Churn Rate %',
      value: '3.2%',
      trend: -0.4,
      comparisonPeriod: 'last week'
    }, {
      title: 'Active Customer %',
      value: '42%',
      trend: 2.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'At-Risk Customer %',
      value: '18.2%',
      trend: -1.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Win-Back Rate',
      value: '12.5%',
      trend: 2.8,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Order Value',
      value: '$76.50',
      trend: 2.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Customers In Queue',
      value: '15',
      trend: -6.3,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Order Time',
      value: '6.4 min',
      trend: -6.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Time in Store',
      value: '15.8 min',
      trend: -3.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Units per Transaction',
      value: '2.8',
      trend: 3.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Discount %',
      value: '8.5%',
      trend: -0.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'House Percent (by qty)',
      value: '32%',
      trend: 3.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Batches with Compliance Variance',
      value: '7',
      trend: -12.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Excessive Adjustments',
      value: '4',
      trend: -20,
      comparisonPeriod: 'last week'
    }, {
      title: 'Average Online Order Value',
      value: '$85.20',
      trend: 3.2,
      comparisonPeriod: 'last week'
    }, {
      title: 'Mobile-vs-Desktop Traffic Split',
      value: '66/34',
      trend: 1.5,
      comparisonPeriod: 'last week'
    }, {
      title: 'Dual Commerce Customers',
      value: '23%',
      trend: 2.8,
      comparisonPeriod: 'last week'
    }],
    month: [
    // Monthly data for all shops...
    {
      title: 'Total Sales',
      value: '$1,645,320',
      trend: 8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Avg Basket',
      value: '$74.12',
      trend: 5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Transactions',
      value: '22,195',
      trend: 3,
      comparisonPeriod: 'last month'
    }, {
      title: 'New Customers',
      value: '2,450',
      trend: 15,
      comparisonPeriod: 'last month'
    }, {
      title: 'Margin',
      value: '42%',
      trend: 2,
      comparisonPeriod: 'last month'
    }, {
      title: 'Inventory Value',
      value: '$645,360',
      trend: -4,
      comparisonPeriod: 'last month'
    }, {
      title: 'Compliance Score',
      value: '93%',
      trend: 2,
      comparisonPeriod: 'last month'
    }, {
      title: 'Open Compliance Issues',
      value: '8',
      trend: -20,
      comparisonPeriod: 'last month'
    }, {
      title: 'Shrinkage Rate',
      value: '1.1%',
      trend: -0.3,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Wait Time',
      value: '4.2 min',
      trend: -8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Fulfillment Speed',
      value: '8.2 min',
      trend: -5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Cart Abandonment %',
      value: '28%',
      trend: -3,
      comparisonPeriod: 'last month'
    }, {
      title: 'Revenue Growth %',
      value: '6.5%',
      trend: 1.8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Inventory Turnover Rate',
      value: '2.6×',
      trend: 0.4,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Days on Hand',
      value: '17.2',
      trend: -2.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Aging Inventory %',
      value: '12.8%',
      trend: -1.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Reorder Compliance %',
      value: '90%',
      trend: 4,
      comparisonPeriod: 'last month'
    }, {
      title: 'Overstock %',
      value: '10.5%',
      trend: -2.8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Expired Rate',
      value: '0.6%',
      trend: -0.3,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Product Grade',
      value: 'A-',
      trend: 4,
      comparisonPeriod: 'last month'
    }, {
      title: 'New Members',
      value: '1,850',
      trend: 18.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'New Loyalty Members',
      value: '780',
      trend: 12.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Customer Lifetime Value (CLV)',
      value: '$435',
      trend: 2.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Churn Rate %',
      value: '2.9%',
      trend: -0.6,
      comparisonPeriod: 'last month'
    }, {
      title: 'Active Customer %',
      value: '45%',
      trend: 4.2,
      comparisonPeriod: 'last month'
    }, {
      title: 'At-Risk Customer %',
      value: '16.5%',
      trend: -2.8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Win-Back Rate',
      value: '14.5%',
      trend: 3.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Order Value',
      value: '$78.20',
      trend: 3.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Customers In Queue',
      value: '12',
      trend: -20,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Order Time',
      value: '5.8 min',
      trend: -10.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Time in Store',
      value: '14.2 min',
      trend: -7.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Units per Transaction',
      value: '3.0',
      trend: 5.2,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Discount %',
      value: '7.2%',
      trend: -1.8,
      comparisonPeriod: 'last month'
    }, {
      title: 'House Percent (by qty)',
      value: '35%',
      trend: 6.5,
      comparisonPeriod: 'last month'
    }, {
      title: 'Batches with Compliance Variance',
      value: '5',
      trend: -28.6,
      comparisonPeriod: 'last month'
    }, {
      title: 'Excessive Adjustments',
      value: '2',
      trend: -33.3,
      comparisonPeriod: 'last month'
    }, {
      title: 'Average Online Order Value',
      value: '$89.50',
      trend: 4.8,
      comparisonPeriod: 'last month'
    }, {
      title: 'Mobile-vs-Desktop Traffic Split',
      value: '64/36',
      trend: 3,
      comparisonPeriod: 'last month'
    }, {
      title: 'Dual Commerce Customers',
      value: '26%',
      trend: 4.2,
      comparisonPeriod: 'last month'
    }]
  };
  function getCurrentData() {
    if (viewMode === 'all') {
      return allShopsData[timeframe as keyof typeof allShopsData] || [];
    }
    return kpiData[currentLocation as keyof typeof kpiData]?.[timeframe as keyof (typeof kpiData)['Fresno']] || [];
  }
  // Get all selected KPI IDs
  const getSelectedKPIIds = (): string[] => {
    return Object.values(availableKPIs).flatMap(kpis => kpis.filter(kpi => kpi.selected).map(kpi => kpi.id));
  };
  // Filter the KPI data based on selected KPIs
  const getFilteredKPIs = () => {
    const currentData = getCurrentData();
    const selectedKPIIds = getSelectedKPIIds();
    // Filter KPIs based on IDs
    return currentData.filter(kpi => {
      // Get the ID for this KPI title
      const kpiId = kpiTitleToIdMap[kpi.title];
      // If we have an ID for this KPI, check if it's selected
      return kpiId && selectedKPIIds.includes(kpiId);
    });
  };
  function handleKPIClick(title: string) {
    setSelectedKPI(selectedKPI === title ? null : title);
  }
  // Handle applying KPI customizations
  const handleApplyCustomization = (selectedKPIs: Record<string, KPIItem[]>) => {
    setAvailableKPIs(selectedKPIs);
  };
  // Toggle expanded view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    // If we're closing the expanded view, also close any open KPI details
    if (isExpanded) {
      setSelectedKPI(null);
    }
  };
  return <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'bg-[#161b22]' : 'bg-[#f0f5f3]'} border ${isDarkMode ? 'border-[#30363d]' : 'border-gray-200'} ${isExpanded ? 'fixed inset-4 z-50' : ''}`} data-id="element-248">
      <div className={`p-4 ${isDarkMode ? 'bg-[#161b22]' : 'bg-[#f0f5f3]'}`} data-id="element-249">
        <div className="flex justify-between items-center" data-id="element-250">
          <div data-id="element-251">
            <h2 className="text-xl font-bold" data-id="element-252">KPIs</h2>
            <p className="text-sm opacity-80" data-id="element-253">
              Your shop's pulse—tap any metric to open levers
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-254">
            <button className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-[#0d1117]' : 'hover:bg-gray-100'}`} onClick={() => setIsCustomizeModalOpen(true)} title="Customize KPIs" data-id="element-255">
              <Settings size={18} data-id="element-256" />
            </button>
            <button className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-[#0d1117]' : 'hover:bg-gray-100'}`} onClick={toggleExpanded} title={isExpanded ? 'Minimize' : 'Expand'} data-id="element-257">
              {isExpanded ? <Minimize size={18} data-id="element-258" /> : <Maximize size={18} data-id="element-259" />}
            </button>
          </div>
        </div>
        <div className="flex justify-between mt-3" data-id="element-260">
          <div className="flex" data-id="element-261">
            <select className={`px-3 py-1.5 rounded-md mr-2 ${isDarkMode ? 'bg-[#0d1117] border-[#30363d]' : 'bg-white border-gray-300'} border`} value={timeframe} onChange={e => setTimeframe(e.target.value)} data-id="element-262">
              <option value="today" data-id="element-263">Today</option>
              <option value="week" data-id="element-264">This Week</option>
              <option value="month" data-id="element-265">This Month</option>
            </select>
          </div>
          <div className="flex" data-id="element-266">
            <button className={`px-3 py-1.5 rounded-md mr-2 ${viewMode === 'current' ? `bg-[#2196f3] text-white` : isDarkMode ? 'bg-[#0d1117]' : 'bg-white'}`} onClick={() => setViewMode('current')} data-id="element-267">
              Current Shop
            </button>
            <button className={`px-3 py-1.5 rounded-md ${viewMode === 'all' ? `bg-[#2196f3] text-white` : isDarkMode ? 'bg-[#0d1117]' : 'bg-white'}`} onClick={() => setViewMode('all')} data-id="element-268">
              All Shops
            </button>
          </div>
        </div>
      </div>
      <div className="p-4" data-id="element-269">
        <div className={`grid gap-4 ${isExpanded ? 'grid-cols-3 md:grid-cols-4 lg:grid-cols-5 max-h-[calc(100vh-240px)] overflow-y-auto' : 'grid-cols-2 md:grid-cols-3 max-h-[450px] overflow-y-auto'}`} data-id="element-270">
          {getFilteredKPIs().map((kpi, index) => <KPITile key={index} {...kpi} onClick={() => handleKPIClick(kpi.title)} definition={kpiDefinitions[kpiTitleToIdMap[kpi.title]]} data-id="element-271" />)}
        </div>
        {getFilteredKPIs().length === 0 && <div className="p-6 text-center" data-id="element-272">
            <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-273">
              No KPIs selected. Click the settings icon to customize your
              metrics.
            </p>
          </div>}
        {!isExpanded && getFilteredKPIs().length > 9 && <div className="mt-4 text-center" data-id="element-274">
            <button className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-[#0d1117] hover:bg-[#161c22]' : 'bg-white hover:bg-gray-100'}`} onClick={toggleExpanded} data-id="element-275">
              Expand and Show all
            </button>
          </div>}
        {selectedKPI && <LeversSection kpiName={selectedKPI} data-id="element-276" />}
      </div>
      {/* Customization Modal */}
      <KPICustomizeModal isOpen={isCustomizeModalOpen} onClose={() => setIsCustomizeModalOpen(false)} onApply={handleApplyCustomization} initialSelectedKPIs={availableKPIs} data-id="element-277" />
    </div>;
};
export default KPIs;